import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminPersonSummary } from "../../api/adminPersons";
import AdminAppLayout from "../../components/admin/AdminAppLayout";
import { useAdminAuth } from "../../context/AdminAuthContext";

export default function AdminDashboardPage() {
  const { user, logout } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setStatsError(null);
    setStatsLoading(true);
    try {
      const summary = await fetchAdminPersonSummary();
      setStats(summary);
    } catch {
      setStatsError("Could not load summary.");
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  return (
    <AdminAppLayout>
      <header className="admin-app-pagehead">
        <div>
          <p className="admin-dashboard-status">Admin</p>
          <h1 className="admin-app-title">Dashboard</h1>
        </div>
      </header>

      <nav className="admin-nav-cards" aria-label="Admin tools">
        <Link to="/admin/guests" className="admin-nav-card">
          <span className="admin-nav-card-kicker">Guests</span>
          <span className="admin-nav-card-title">People &amp; invitations</span>
          <span className="admin-nav-card-desc">
            Save-the-date responses, statuses, mark invited when ready.
          </span>
        </Link>
        <Link to="/admin/change-password" className="admin-nav-card">
          <span className="admin-nav-card-kicker">Security</span>
          <span className="admin-nav-card-title">Change password</span>
          <span className="admin-nav-card-desc">Update the password for this admin account.</span>
        </Link>
        <Link to="/admin/email-test" className="admin-nav-card">
          <span className="admin-nav-card-kicker">Email</span>
          <span className="admin-nav-card-title">Send test email</span>
          <span className="admin-nav-card-desc">Try SES: send the invitation email to any address (sandbox rules apply).</span>
        </Link>
      </nav>

      <section className="admin-panel" aria-labelledby="admin-overview-heading">
        <div className="admin-panel-headrow">
          <h2 id="admin-overview-heading" className="admin-panel-heading">
            At a glance
          </h2>
          {stats?.generatedAt ? (
            <p className="admin-dash-statmeta">
              Updated {new Date(stats.generatedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
            </p>
          ) : null}
        </div>
        {statsLoading ? (
          <p className="admin-guests-muted">Loading summary…</p>
        ) : statsError ? (
          <p className="admin-guests-muted" role="status">
            {statsError}
          </p>
        ) : stats ? (
          <ul className="admin-dash-statcards">
            <li>
              <Link to="/admin/guests" className="admin-dash-statcard admin-dash-statcard--total">
                <span className="admin-dash-statcard-value">{stats.total}</span>
                <span className="admin-dash-statcard-label">People total</span>
                <span className="admin-dash-statcard-cta">Open list →</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/guests" className="admin-dash-statcard admin-dash-statcard--guest">
                <span className="admin-dash-statcard-value">{stats.guest}</span>
                <span className="admin-dash-statcard-label">Guest</span>
                <span className="admin-dash-statcard-cta">Review →</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/guests" className="admin-dash-statcard admin-dash-statcard--invited">
                <span className="admin-dash-statcard-value">{stats.invited}</span>
                <span className="admin-dash-statcard-label">Invited</span>
                <span className="admin-dash-statcard-cta">Review →</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/guests" className="admin-dash-statcard admin-dash-statcard--attendee">
                <span className="admin-dash-statcard-value">{stats.attendee}</span>
                <span className="admin-dash-statcard-label">Attendee</span>
                <span className="admin-dash-statcard-cta">Review →</span>
              </Link>
            </li>
          </ul>
        ) : null}
      </section>

      <section className="admin-panel" aria-labelledby="admin-account-heading">
        <h2 id="admin-account-heading" className="admin-panel-heading">
          Your account
        </h2>
        <p className="admin-dashboard-sub">Signed in as</p>
        <dl className="admin-user-details">
          <div>
            <dt>Email</dt>
            <dd>{user?.email}</dd>
          </div>
          {user?.displayName ? (
            <div>
              <dt>Display name</dt>
              <dd>{user.displayName}</dd>
            </div>
          ) : null}
          <div>
            <dt>Role</dt>
            <dd>{user?.role}</dd>
          </div>
          <div>
            <dt>User id</dt>
            <dd>{user?.id}</dd>
          </div>
        </dl>
        <pre className="admin-user-raw">{JSON.stringify(user, null, 2)}</pre>
        <button type="button" className="admin-logout-btn" onClick={() => void logout()}>
          Sign out
        </button>
      </section>
    </AdminAppLayout>
  );
}
